import { Star, Calendar, Clock, Plane } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useFeaturedPackages } from '../hooks/useFeaturedPackages'
import Newsletter from '../components/Newsletter'
import OurExpertise from '../components/PopularDestinations'
import { useEffect, useState } from 'react'
import JsonLd from '../components/JsonLd'

const HERO_IMAGES = ['/hero-1.jpg', '/hero-2.jpg', '/hero-3.jpg']

export default function Home() {
  const { franchiseData } = useFranchiseData()
  const { featuredPackages, loading: packagesLoading, error: packagesError } = useFeaturedPackages()
  const [heroIndex, setHeroIndex] = useState(0)
  const [heroReady, setHeroReady] = useState<boolean[]>(() => HERO_IMAGES.map((_, i) => i === 0))

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((prev) => {
        const next = (prev + 1) % HERO_IMAGES.length
        return heroReady[next] ? next : prev
      })
    }, 4000)
    return () => clearInterval(interval)
  }, [heroReady])

  // Helper function similar to Packages.tsx
  const getImageUrl = (pkg: any) => {
    if (pkg.cover_photo?.large_url) {
      return pkg.cover_photo.large_url
    }
    if (pkg.cover_photo?.url) {
      return pkg.cover_photo.url
    }
    if (pkg.scraped_cover_photo) {
      return pkg.scraped_cover_photo
    }
    return null
  }

  return (
    <div className="bg-white">
      <Helmet>
        <title>Chelsea Travel | Holiday Packages & Tailor‑Made Trips</title>
        <meta
          name="description"
          content="Personalized travel experiences crafted by South Africa's trusted travel experts. Explore packages and plan your next journey."
        />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.origin + '/' : '/'}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Chelsea Travel | Holiday Packages & Tailor‑Made Trips" />
        <meta
          property="og:description"
          content="Personalized travel experiences crafted by South Africa's trusted travel experts."
        />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/'}
        />
        <meta property="og:image" content="/image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Home',
          url: typeof window !== 'undefined' ? window.location.href : '/',
          description:
            "Personalized travel experiences crafted by South Africa's trusted travel experts.",
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: typeof window !== 'undefined' ? window.location.origin + '/' : '/',
              },
            ],
          },
        }}
      />
      {/* Hero section */}
      <div className="relative isolate px-6 pt-14 lg:px-8 min-h-[600px] flex items-center justify-center overflow-hidden bg-gray-900">
        {HERO_IMAGES.map((src, index) => (
          <img
            key={src}
            src={src}
            alt=""
            aria-hidden="true"
            fetchPriority={index === 0 ? 'high' : 'low'}
            decoding={index === 0 ? 'sync' : 'async'}
            onLoad={() =>
              setHeroReady((prev) => {
                if (prev[index]) return prev
                const next = [...prev]
                next[index] = true
                return next
              })
            }
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in-out"
            style={{ opacity: index === heroIndex ? 1 : 0 }}
          />
        ))}
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 mx-auto max-w-2xl py-32 sm:py-48 lg:py-56 text-center">
          <h1 className="text-4xl sm:text-6xl font-extrabold text-white leading-tight">
            Your Journey to <br />
            <span className="block" style={{ color: '#40C2E3' }}>
              Extraordinary Begins Here
            </span>
          </h1>
          <p className="mt-6 text-lg text-white font-medium">
            Personalized travel experiences crafted by South Africa's trusted travel experts
          </p>
          <div className="mt-10 flex flex-col items-center justify-center">
            <a
              href="/contact"
              className="btn border-2 text-[#40C2E3] border-[#40C2E3] text-lg font-semibold px-8 py-3 rounded-lg hover:bg-[#40C2E3] hover:text-white transition flex items-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-9 4v8"
                />
              </svg>
              Speak to an Expert
            </a>
          </div>
        </div>
      </div>

      {/* Featured Packages section */}
      <div id="offers" className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Featured Packages
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Discover our handpicked selection of exceptional travel experiences.
            </p>
          </div>

          {packagesLoading ? (
            <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col items-start animate-pulse">
                  <div className="aspect-[16/9] w-full rounded-2xl bg-gray-300 sm:aspect-[2/1] lg:aspect-[3/2]"></div>
                  <div className="max-w-xl w-full">
                    <div className="mt-8 h-4 bg-gray-300 rounded w-1/3"></div>
                    <div className="mt-3 h-6 bg-gray-300 rounded w-2/3"></div>
                    <div className="mt-5 h-12 bg-gray-300 rounded"></div>
                    <div className="mt-4 h-4 bg-gray-300 rounded w-1/2"></div>
                    <div className="mt-4 flex justify-between items-center">
                      <div className="h-8 bg-gray-300 rounded w-1/3"></div>
                      <div className="h-10 bg-gray-300 rounded w-24"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : packagesError ? (
            <div className="mx-auto mt-16 max-w-2xl text-center">
              <h3 className="text-lg font-semibold text-gray-900">Unable to load packages</h3>
              <p className="mt-2 text-sm text-gray-600">{packagesError}</p>
              <button onClick={() => window.location.reload()} className="mt-4 btn-primary">
                Try Again
              </button>
            </div>
          ) : featuredPackages.length === 0 ? (
            <div className="mx-auto mt-16 max-w-2xl text-center">
              <h3 className="text-lg font-semibold text-gray-900">
                No featured packages available
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Check back soon for our latest travel deals.
              </p>
              <a href="/packages" className="mt-4 btn-primary">
                View All Packages
              </a>
            </div>
          ) : (
            <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {featuredPackages.slice(0, 3).map((pkg) => {
                const imageUrl = getImageUrl(pkg)

                return (
                  <article key={pkg.id} className="flex flex-col items-start group">
                    <Link to={`/packages/${pkg.slug}`} className="w-full">
                      <div className="relative w-full">
                        {/* Image with franchise accent color background - match Packages.tsx */}
                        <div
                          className="aspect-[16/9] w-full rounded-2xl object-cover sm:aspect-[2/1] lg:aspect-[3/2] flex items-center justify-center group-hover:shadow-lg transition-shadow duration-300"
                          style={{
                            backgroundColor: franchiseData?.accent_color || '#58b8cd',
                            backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                        >
                          {!imageUrl && <Plane className="h-12 w-12 text-white opacity-60" />}
                        </div>
                        <div className="absolute top-4 left-4 bg-white px-3 py-1 rounded-full text-sm font-medium">
                          {pkg.category}
                        </div>
                        {pkg.nights && (
                          <div className="absolute top-4 right-4 bg-[#58b8cd] text-white px-3 py-1 rounded-full text-sm font-medium">
                            {pkg.nights} {pkg.nights === 1 ? 'Night' : 'Nights'}
                          </div>
                        )}
                      </div>
                    </Link>

                    <div className="max-w-xl w-full">
                      <div className="mt-8 flex items-center gap-x-4 text-xs">
                        <div className="flex items-center gap-1">
                          {Array.from({ length: pkg.rating || 5 }).map((_, i) => (
                            <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                        <span className="text-gray-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {pkg.duration}
                        </span>
                      </div>
                      <div className="group relative">
                        <Link to={`/packages/${pkg.slug}`}>
                          <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-gray-600 transition-colors">
                            {pkg.name}
                          </h3>
                        </Link>
                        <p className="mt-5 line-clamp-3 text-sm leading-6 text-gray-600">
                          {pkg.intro || pkg.description}
                        </p>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {pkg.includes?.map((item: string) => (
                          <span
                            key={item}
                            className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium"
                            style={{
                              backgroundColor: `#58b8cd20`,
                              color: '#58b8cd',
                            }}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                      <div className="mt-4 flex items-center gap-x-4">
                        <div className="flex items-center gap-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{pkg.dates}</span>
                        </div>
                      </div>
                      <div className="mt-6 flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="text-2xl font-bold" style={{ color: '#58b8cd' }}>
                            From {pkg.formatted_price} pps
                          </span>
                          {pkg.total_pax && (
                            <span className="text-xs text-gray-500">{pkg.total_pax}</span>
                          )}
                        </div>
                        <Link to={`/packages/${pkg.slug}`} className="btn-primary">
                          View Details
                        </Link>
                      </div>
                    </div>
                  </article>
                )
              })}
            </div>
          )}
          <div className="mt-12 text-center">
            <a
              href="/packages"
              className="text-sm font-semibold leading-6"
              style={{ color: '#58b8cd' }}
            >
              See all packages <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </div>

      {/* Our Expertise Carousel */}
      <OurExpertise />

      {/* Add extra space between sections */}
      <div className="my-24" />

      {/* Newsletter section */}
      <Newsletter buttonColor={franchiseData?.button_color || '#58b8cd'} />
    </div>
  )
}
